import { Error } from "@tql/ui-components-react/kendo/labels";
import type { ReactNode } from "react";
import { useContext } from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import type {
	OnChange,
	QuoteStateType,
} from "~/shared/types/quote-details-types";

import { QuoteDetailsContext } from "../../../../context/quote-details-context.tsx";
import { CustomerDetailsBar } from "../customer-details-bar/customer-details-bar.tsx";
import { CustomerInfo } from "../customer-info/customer-info.tsx";
import { TemplateActions } from "../template-actions/template-actions.tsx";
import styles from "./customer-select-row.module.css";

type CustomerSelectRowProps = {
	customerCleared: boolean;
	forceExternal?: boolean;
	getChangeHandler: (
		field: keyof QuoteStateType,
	) => (change: { value: OnChange }) => void;
	hideTemplates?: boolean;
	isParentLoading: boolean;
	onClearValidation?: () => void;
	onClearTemplate: () => void;
	onToggleTemplatesModal: () => void;
	redirectRoute?: string;
	setCustomerCleared: (value: boolean) => void;
	showValidationError?: boolean;
	simulateMode?: boolean;
	templateMode?: boolean;
	leadingContent?: ReactNode;
};

export const CustomerSelectRow = ({
	customerCleared,
	forceExternal = false,
	getChangeHandler,
	hideTemplates = false,
	isParentLoading,
	onClearValidation,
	onClearTemplate,
	onToggleTemplatesModal,
	redirectRoute,
	setCustomerCleared,
	showValidationError = false,
	simulateMode = false,
	templateMode,
	leadingContent,
}: CustomerSelectRowProps) => {
	const { isInternal } = useDeploymentType();
	const { quoteState } = useContext(QuoteDetailsContext);
	const customerHasId = Boolean(quoteState?.customer?.id);
	// CustomerInfo renders nothing for external users; skip its wrapper too so an
	// empty flex item doesn't add a phantom `gap` before My Templates, shifting it
	// right of the true left edge (misaligned with Ship Date below).
	const showCustomerInfo = isInternal && !forceExternal;

	return (
		<div className={styles.customerSelectRow}>
			<div className={styles.customerSelectInputsRow}>
				{showCustomerInfo ? (
					<div className={"customer-select-info-item"}>
						<CustomerInfo
							customerCleared={customerCleared}
							forceExternal={forceExternal}
							getChangeHandler={getChangeHandler}
							onClearValidation={onClearValidation}
							redirectRoute={redirectRoute}
							setCustomerCleared={setCustomerCleared}
							showValidationError={showValidationError}
							templateMode={templateMode}
						/>
					</div>
				) : null}
				{leadingContent}
				{hideTemplates ? null : (
					<div className={styles.templatesSection}>
						<TemplateActions
							onClearTemplate={onClearTemplate}
							onToggleTemplatesModal={onToggleTemplatesModal}
							simulateMode={simulateMode}
							templateMode={templateMode ?? false}
						/>
					</div>
				)}
				{isInternal && customerHasId && !customerCleared ? (
					<div className={styles.customerDetailsBarWrapper}>
						<CustomerDetailsBar isParentLoading={isParentLoading} />
					</div>
				) : null}
			</div>

			{showValidationError ? (
				<Error id={"customer_error"}>Customer is required</Error>
			) : null}
		</div>
	);
};
